import Axios from "axios";
import {
  Dimensions,
  ProductDescription,
  ProductPackageMeasures,
} from "../ecommerce/pages/ProductInformation/models/productDescription";
import { handleError } from "../utils/utils";
import type { InventoryResponse, TransferPayload, TransferLogEntry, TransferHistoryResponse, UpdatePricePayload, UpdateStockPayload } from "../privatePages/Inventory/components/ProductsTable/types";
import type {
  ReservationListParams,
  ReservationListResponse,
  ReservationDetail,
  CreateReservationPayload,
  EditReservationPayload,
  ReservationHistoryParams,
  ReservationHistoryResponse,
  AvailableStockResponse,
} from "../privatePages/Reservations/types";

const throwRequestError = (error: any, fallbackMessage: string) => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  handleError(new Error(message));
};

export async function getAllColors() {
  try {
    const colors = await Axios("/v2/products/colors");
    return colors.data;
  } catch (error) {
    handleError(error);
  }
}

export async function getAllAddresses() {
  try {
    const addresses = await Axios("/v2/suppliers/storage-address");
    return addresses.data;
  } catch (error) {
    handleError(error);
  }
}

// Supplier

export async function getAllEcommercePreferences() {
  try {
    const preferences = await Axios("/v2/suppliers/e-commerce");
    return preferences.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateEcommercePreferences(ecommerceData: FormData) {
  try {
    const response = await Axios.patch(
      "/v2/suppliers/e-commerce",
      ecommerceData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Get Wompi Public key
export async function getWompiPublicKey() {
  try {
    const response = await Axios.get("/v2/suppliers/wompi-key");
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Get Wompi Public Key By Supplier
export async function getWompiPublicKeyBySupplier(supplierName: string) {
  try {
    supplierName = supplierName.replace(/-/g, " ");
    const response = await Axios.get(`/v2/suppliers/wompi-key/${supplierName}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Update Wompi Public Key
export async function updateWompiPublicKey(wompiKey: string) {
  try {
    const response = await Axios.patch("/v2/suppliers/wompi-key", { wompiKey });
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Get Envia Public key
export async function getEnviaPublicKey() {
  try {
    const response = await Axios.get("/v2/suppliers/envia-key");
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Update Envia Public Key
export async function updateEnviaPublicKey(enviaKey: string) {
  try {
    const response = await Axios.patch("/v2/suppliers/envia-key", { enviaKey });
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// Feature Flags

export async function getAdminSuppliers() {
  try {
    const response = await Axios.get("/v1/suppliers");
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los proveedores");
  }
}

export async function getSupplierFeatures(supplierId: number) {
  try {
    const response = await Axios.get(`/v2/features/suppliers/${supplierId}`);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los feature flags del proveedor");
  }
}

export async function getMyFeatures() {
  try {
    const response = await Axios.get("/v2/features/my");
    return response.data;
  } catch (error: any) {
    throwRequestError(
      error,
      "Error al cargar los feature flags del proveedor actual"
    );
  }
}

export async function setSupplierFeature(
  supplierId: number,
  featureId: number,
  isEnabled: boolean
) {
  try {
    const response = await Axios.put(
      `/v2/features/suppliers/${supplierId}/${featureId}`,
      {
        is_enabled: isEnabled,
      }
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el feature flag");
  }
}

export async function updateProductGeneralInfo(
  productId: number,
  productData: ProductDescription
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/general`,
      productData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateProductMeasurements(
  productId: number,
  productMeasurements: Dimensions
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/product-measurements`,
      productMeasurements
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function UpdateProductPackageMeasures(
  productId: number,
  packingPresentationId: number,
  productPackageMeasures: ProductPackageMeasures
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/packaging-measurements/${packingPresentationId}`,
      productPackageMeasures
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateProductPrincipalImage(
  productId: number,
  imageId: number | null,
  imageFile: FormData
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/principal-image/${imageId}`,
      imageFile
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateProductImages(
  productId: number,
  formDataForRequest: FormData
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/ambient-images`,
      formDataForRequest
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function updateProductTechnicalSheet(
  productId: number,
  technicalSheet: FormData
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/products/${productId}/technical-sheet`,
      technicalSheet
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// ============================================================
// ECOMMERCE BANNER
// ============================================================

export async function addEcommerceBanner(bannerFile: FormData) {
  try {
    const response = await Axios.post(
      `/v2/suppliers/e-commerce/banner`,
      bannerFile
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function deleteEcommerceBanner(bannerId: number) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/e-commerce/banner/delete/${bannerId}`
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// ============================================================
// SALES PERSON
// ============================================================

export async function getSalesPerson() {
  try {
    const response = await Axios.get("/v2/suppliers/salespeople");
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function getSalesPersonById(id: string) {
  try {
    const response = await Axios.get(`/v2/suppliers/salesperson/${id}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function createSalesPerson(
  salesPersonData: FormData,
  companyName: string
) {
  try {
    // Extract values from FormData
    const email = salesPersonData.get("email");
    const phone = salesPersonData.get("phone");
    const cedula = salesPersonData.get("cedula");

    //Check if the email is already registered
    let dataToCheck = {
      email,
      phone,
      cedula,
    };

    // when client tries to register with an existing user
    await Axios.post("/v1/users/check", dataToCheck);

    const response = await Axios.post(
      `/v2/salespeople/salesperson/${companyName}`,
      salesPersonData
    );

    return response.data;
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      "Ocurrió un error al procesar la solicitud. Por favor, intenta nuevamente.";
    handleError(message);
  }
}

export async function updateSalesPerson(
  salesPersonId: number,
  salesPersonData: SalesPerson
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/salesperson/${salesPersonId}`,
      salesPersonData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function deleteSalesPerson(salesPersonId: number) {
  try {
    const response = await Axios.delete(
      `/v2/suppliers/salesperson/${salesPersonId}`
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function activateSalesPerson(
  salesPersonId: number,
  salesPersonData: SalesPerson
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/salesperson/activate/${salesPersonId}`,
      salesPersonData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function approveSalesPerson(
  salesPersonId: number,
  salesPersonData: SalesPerson
) {
  try {
    const response = await Axios.patch(
      `/v2/suppliers/salesperson/approve/${salesPersonId}`,
      salesPersonData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function getSalespersonProfile() {
  try {
    const response = await Axios.get("/v2/salespeople/profile/salesperson");
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function createSalespersonQuote(payload: {
  quotation: any;
  totals: any;
}) {
  try {
    const response = await Axios.post("/v2/salespeople/quote", payload);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function sendSalespersonQuoteEmail(payload: {
  pdfUrl: string;
  clientEmail: string;
  clientName: string;
  advisorEmail: string;
}) {
  try {
    const response = await Axios.post("/v2/salespeople/quote/email", payload);
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

export async function completeTraining() {
  try {
    const response = await Axios.patch(
      "/v2/salespeople/salesperson/training-status",
      {
        is_training_finished: true,
      }
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// ============================================================
// NAVBAR LINKS
// ============================================================

// ============================== CREATE NAVBAR LINK
export async function createNavbarLink(newLink: NewNavbarLink) {
  try {
    const { data } = await Axios.post("/v2/suppliers/navigation-link", newLink);
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== GET NAVBAR LINKS
export async function getNavbarLinks() {
  try {
    const { data } = await Axios.get("/v2/suppliers/navigation-link");
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== DELETE NAVBAR LINK
export async function deleteNavbarLink(linkId: number) {
  try {
    await Axios.delete(`/v2/suppliers/navigation-link/${linkId}`);
    return { status: "Ok" };
  } catch (error) {
    handleError(error);
  }
}

// ============================== UPDATE NAVBAR LINK
export async function updateNavbarLink(updatedLink: NavbarLink) {
  try {
    const { data } = await Axios.patch(
      `/v2/suppliers/navigation-link/${updatedLink.id}`,
      {
        name: updatedLink.name,
        url: updatedLink.url,
      }
    );
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== GET SOCIAL MEDIA
export async function getSocialMedia() {
  try {
    const response = await Axios.get("/v2/suppliers/social-media");
    return response.data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== GET SOCIAL MEDIA LINKS

export async function getSocialMediaLinks() {
  try {
    const { data } = await Axios.get("/v2/suppliers/social-media-links");
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== CREATE SOCIAL MEDIA LINKS

export async function createSocialMediaLink(newLink: NewSocialMediaLink) {
  try {
    const { data } = await Axios.post(
      "/v2/suppliers/social-media-links",
      newLink
    );
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================== DELETE SOCIAL MEDIA LINKS

export async function deleteSocialMediaLink(socialMediaLinkId: number) {
  try {
    await Axios.patch(`/v2/suppliers/social-media-links/${socialMediaLinkId}`);
    return { status: "Ok" };
  } catch (error) {
    handleError(error);
  }
}

// ============================== UPDATE SOCIAL MEDIA LINKS

export async function updateSocialMediaLink({
  id,
  socialMediaTypeId,
  url,
}: UpdateSocialMediaLink) {
  try {
    const { data } = await Axios.put(`/v2/suppliers/social-media-links/${id}`, {
      socialMediaTypeId,
      url,
    });
    return data;
  } catch (error) {
    handleError(error);
  }
}

// ============================================================
// CHAT MESSAGES NOTIFICATIONS
// ============================================================

// ============================== GET UNREAD MESSAGES
export async function getUnreadMessages(): Promise<PendingNotificationsResponse> {
  try {
    const { data } = await Axios.get<PendingNotificationsResponse>(
      `/v1/chat-notification/unread`
    );
    return data;
  } catch (error) {
    handleError(error);
    return [];
  }
}

// ============================== MARK NOTIFICATION AS READ
export async function markNotificationAsRead(salesConsultancyHashid: string) {
  try {
    await Axios.patch(`/v1/chat-notification/${salesConsultancyHashid}/read`);
    return true;
  } catch (e) {
    return false;
  }
}

// ============================== MARK ALL NOTIFICATIONS AS READ
export async function markAllNotificationsAsRead(
  salesConsultancyHashids: string[]
) {
  return Promise.all(
    salesConsultancyHashids.map((id) =>
      Axios.patch(`/v1/chat-notification/${id}/read`).catch(() => {})
    )
  );
}

// ============================================================
// TRAINING SESSIONS
// ============================================================

export async function getUpcomingTrainingSessions() {
  try {
    const response = await Axios.get(
      "/v2/salespeople/training-sessions/upcoming"
    );
    return response.data;
  } catch (error) {
    handleError(error);
    return { collection: [], pagination: null };
  }
}

// ============================================================
// RBAC
// ============================================================

export async function getRbacPermissions() {
  try {
    const response = await Axios.get("/v2/rbac/permissions");
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los permisos");
  }
}

export async function getRbacRoles() {
  try {
    const response = await Axios.get("/v2/rbac/roles");
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los roles");
  }
}

export async function createRbacRole(data: {
  name: string;
  description?: string;
}) {
  try {
    const response = await Axios.post("/v2/rbac/roles", data);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al crear el rol");
  }
}

export async function updateRbacRole(
  roleId: number,
  data: {
    name?: string;
    description?: string;
  }
) {
  try {
    const response = await Axios.patch(`/v2/rbac/roles/${roleId}`, data);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el rol");
  }
}

export async function deleteRbacRole(roleId: number) {
  try {
    const response = await Axios.delete(`/v2/rbac/roles/${roleId}`);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al eliminar el rol");
  }
}

export async function setRbacRolePermissions(
  roleId: number,
  permissionIds: number[]
) {
  try {
    const response = await Axios.put(`/v2/rbac/roles/${roleId}/permissions`, {
      permission_ids: permissionIds,
    });
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar los permisos del rol");
  }
}

export async function getRbacMembers() {
  try {
    const response = await Axios.get("/v2/rbac/members");
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los empleados");
  }
}

export async function createRbacMember(data: {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  cedula?: string;
  role_ids: number[];
}) {
  try {
    const response = await Axios.post("/v2/rbac/members", data);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al crear el empleado");
  }
}

export async function toggleRbacMemberActive(
  memberId: number,
  isActive: boolean
) {
  try {
    const response = await Axios.patch(`/v2/rbac/members/${memberId}`, {
      is_active: isActive,
    });
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el estado del empleado");
  }
}

export async function deleteRbacMember(memberId: number) {
  try {
    const response = await Axios.delete(`/v2/rbac/members/${memberId}`);
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al eliminar el empleado");
  }
}

export async function setRbacMemberRoles(memberId: number, roleIds: number[]) {
  try {
    const response = await Axios.put(`/v2/rbac/members/${memberId}/roles`, {
      role_ids: roleIds,
    });
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar los roles del empleado");
  }
}

// ============================================================
// INVENTORY MULTI-LOCATION
// ============================================================

/** GET /v2/inventory — returns products with per-location stock */
export async function getInventory(): Promise<InventoryResponse> {
  try {
    const response = await Axios.get<InventoryResponse>("/v2/inventory");
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar el inventario");
    return { products: [], storageAddresses: [] };
  }
}

/** POST /v2/inventory/transfer — atomically moves stock between locations */
export async function transferStock(payload: TransferPayload): Promise<void> {
  try {
    await Axios.post("/v2/inventory/transfer", payload);
  } catch (error: any) {
    throwRequestError(error, "Error al transferir el stock");
  }
}

/** PATCH /v2/inventory/products/:id/price — updates a product's price + taxed_price */
export async function updateProductPrice(
  productId: number,
  payload: UpdatePricePayload
): Promise<{ id: number; name: string; price: number; taxed_price: number }> {
  try {
    const { data } = await Axios.patch(
      `/v2/inventory/products/${productId}/price`,
      payload
    );
    return data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el precio");
    // Unreachable — throwRequestError re-throws — but keeps TS happy
    throw error;
  }
}

/** PATCH /v2/inventory/stock — upserts per-warehouse stock and syncs units_available */
export async function updateProductStock(
  payload: UpdateStockPayload
): Promise<{
  product_id: number;
  storage_address_id: number;
  quantity: number;
  units_available: number;
}> {
  try {
    const { data } = await Axios.patch("/v2/inventory/stock", payload);
    return data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el stock");
    // Unreachable — throwRequestError re-throws — but keeps TS happy
    throw error;
  }
}

/**
 * GET /v2/inventory/transfer-history — audit log of stock transfers.
 * Optional query param: product_id filters to a single product.
 * Optional query params: page, limit for pagination.
 * When page/limit are provided, returns a paginated envelope.
 * When omitted, returns a flat array (backward compatible).
 */
export async function getTransferHistory(
  productId?: number,
  page?: number,
  limit?: number
): Promise<TransferLogEntry[] | TransferHistoryResponse> {
  try {
    const params: Record<string, number> = {};
    if (productId != null) params.product_id = productId;
    if (page != null) params.page = page;
    if (limit != null) params.limit = limit;

    const response = await Axios.get<TransferLogEntry[] | TransferHistoryResponse>(
      "/v2/inventory/transfer-history",
      { params }
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar el historial de traslados");
    return [];
  }
}

/** GET /v2/inventory/export — generates a PDF of the supplier inventory and returns an S3 URL */
export async function exportInventoryPdf(): Promise<{ url: string }> {
  const { data } = await Axios.get("/v2/inventory/export");
  return data;
}

/** GET /v2/inventory/transfer-history/export — generates a PDF of the full transfer history and returns an S3 URL */
export async function exportTransferHistoryPdf(): Promise<{ url: string }> {
  const { data } = await Axios.get("/v2/inventory/transfer-history/export");
  return data;
}

// ============================================================
// RBAC — current user
// ============================================================

export interface MyPermissionsResponse {
  permissions: string[];
}

/** GET /v2/rbac/my/permissions
 * Returns the permission slugs assigned to the currently authenticated user.
 * Catches all errors silently and returns an empty permissions array so that
 * callers always receive a valid shape — never undefined or an exception.
 */
export async function getMyPermissions(): Promise<MyPermissionsResponse> {
  try {
    const response = await Axios.get<MyPermissionsResponse>(
      "/v2/rbac/my/permissions"
    );
    return response.data;
  } catch {
    return { permissions: [] };
  }
}

/** DELETE /v2/supplier/products/:id — soft-deletes a product (sets deleted_at) */
export async function deleteProduct(productId: number): Promise<void> {
  try {
    await Axios.delete(`/v2/supplier/products/${productId}`);
  } catch (error: any) {
    throwRequestError(error, "Error al eliminar el producto");
  }
}

// ============================================================
// COMPLEMENTARY PRODUCTS
// ============================================================

export interface ComplementaryBody {
  complementary_product_id: number;
  quantity: number;
  unit: "m2" | "unidad";
}

export interface ComplementaryUpdateBody {
  quantity?: number;
  unit?: "m2" | "unidad";
}

export async function getComplementaries(productId: number) {
  try {
    const response = await Axios.get(
      `/v2/supplier/products/${productId}/complementary`
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar los complementarios");
  }
}

export async function addComplementary(
  productId: number,
  body: ComplementaryBody
) {
  try {
    const response = await Axios.post(
      `/v2/supplier/products/${productId}/complementary`,
      body
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al agregar el complementario");
  }
}

export async function updateComplementary(
  productId: number,
  id: number,
  body: ComplementaryUpdateBody
) {
  try {
    const response = await Axios.patch(
      `/v2/supplier/products/${productId}/complementary/${id}`,
      body
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al actualizar el complementario");
  }
}

export async function deleteComplementary(productId: number, id: number) {
  try {
    const response = await Axios.delete(
      `/v2/supplier/products/${productId}/complementary/${id}`
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al eliminar el complementario");
  }
}

// ============================================================
// RESERVATIONS (Approval-Based Flow)
// ============================================================

/** GET /v2/reservations — paginated list of reservations */
export async function getReservations(
  params?: ReservationListParams
): Promise<ReservationListResponse> {
  try {
    const response = await Axios.get<ReservationListResponse>(
      "/v2/reservations",
      { params }
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar las reservas");
    return { results: [], total: 0, page: 1, totalPages: 0 };
  }
}

/** GET /v2/reservations/:id — full reservation detail with items */
export async function getReservation(
  id: number
): Promise<ReservationDetail> {
  try {
    const response = await Axios.get<{ reservation: ReservationDetail; items: any[] }>(
      `/v2/reservations/${id}`
    );
    // Backend returns { reservation, items } — merge into flat ReservationDetail
    return {
      ...response.data.reservation,
      items: response.data.items,
    };
  } catch (error: any) {
    throwRequestError(error, "Error al cargar la reserva");
    throw error;
  }
}

/** POST /v2/reservations — create a new pending reservation */
export async function createReservation(
  payload: CreateReservationPayload
): Promise<ReservationDetail> {
  try {
    const response = await Axios.post<ReservationDetail>(
      "/v2/reservations",
      payload
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al crear la reserva");
    throw error;
  }
}

/** PATCH /v2/reservations/:id — edit a pending reservation */
export async function editReservation(
  id: number,
  payload: EditReservationPayload
): Promise<ReservationDetail> {
  try {
    const response = await Axios.patch<ReservationDetail>(
      `/v2/reservations/${id}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al editar la reserva");
    throw error;
  }
}

/** POST /v2/reservations/:id/approve — approve and decrement stock */
export async function approveReservation(
  id: number
): Promise<ReservationDetail> {
  try {
    const response = await Axios.post<ReservationDetail>(
      `/v2/reservations/${id}/approve`
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al aprobar la reserva");
    throw error;
  }
}

/** POST /v2/reservations/:id/reject — reject a pending reservation */
export async function rejectReservation(
  id: number,
  reason?: string
): Promise<ReservationDetail> {
  try {
    const response = await Axios.post<ReservationDetail>(
      `/v2/reservations/${id}/reject`,
      reason ? { reason } : {}
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al rechazar la reserva");
    throw error;
  }
}

/** GET /v2/reservations/history — paginated audit trail */
export async function getReservationHistory(
  params?: ReservationHistoryParams
): Promise<ReservationHistoryResponse> {
  try {
    const response = await Axios.get<ReservationHistoryResponse>(
      "/v2/reservations/history",
      { params }
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar el historial de reservas");
    return { results: [], total: 0, page: 1, totalPages: 0 };
  }
}

/**
 * GET /v2/reservations/available-stock?items=PID:AID,PID:AID
 * Returns the physical, pending, and available stock for each product+address pair.
 * `items` is a comma-joined list of "product_id:storage_address_id" pairs.
 */
export async function getAvailableStock(
  items: string
): Promise<AvailableStockResponse> {
  try {
    const response = await Axios.get<AvailableStockResponse>(
      "/v2/reservations/available-stock",
      { params: { items } }
    );
    return response.data;
  } catch (error: any) {
    throwRequestError(error, "Error al cargar el stock disponible");
    return { items: [] };
  }
}
